import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { Sum } from 'components/atoms/CustomSVGIcon';
import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { contactAgeIds } from 'features/views/semaphore/constants';
import {
	getSemaphoreAllMonthsTotalSum,
	getSemaphoreClientsAllMonthsSumByStateIds,
	getSemaphoreInfoGraphics,
	getSemaphoreTransitionCountsByStateIds,
} from 'features/views/semaphore/selectors';
import { equalOrZero } from 'utils/equalOrThreeDashes';

import ImpendingStateTableRow from './ImpendingStateTableRow';

const { oneToTwoYears, threeToFourYears, fiveToSixYears, sevenAndLaterYears } = contactAgeIds;

export const ImpendingStateTable = () => {
	const { firstMonth, secondMonth, thirdMonth } = useSelector(getSemaphoreInfoGraphics);

	// sums for individual sections
	const greenToOrangeClientSum = useSelector((state) =>
		getSemaphoreClientsAllMonthsSumByStateIds(state, oneToTwoYears, threeToFourYears),
	);
	const orangeToRedClientSum = useSelector((state) =>
		getSemaphoreClientsAllMonthsSumByStateIds(state, threeToFourYears, fiveToSixYears),
	);
	const redToGrayClientSum = useSelector((state) =>
		getSemaphoreClientsAllMonthsSumByStateIds(state, fiveToSixYears, sevenAndLaterYears),
	);

	// count data for individual sections
	const greenToOrangeData = useSelector((state) =>
		getSemaphoreTransitionCountsByStateIds(state, oneToTwoYears, threeToFourYears),
	);
	const orangeToRedData = useSelector((state) =>
		getSemaphoreTransitionCountsByStateIds(state, threeToFourYears, fiveToSixYears),
	);

	const redToGrayData = useSelector((state) =>
		getSemaphoreTransitionCountsByStateIds(state, fiveToSixYears, sevenAndLaterYears),
	);

	const allMonthsTotalSum = useSelector(getSemaphoreAllMonthsTotalSum);

	// handleClickData = transitioncodes for individual options

	const finalData = [
		{
			tooltip: getLabelMessage('semaphore:clientTransferGreenToOrange'),
			fromColor: '#27ae60',
			toColor: '#f57f17',
			data: greenToOrangeData,
			sumValue: greenToOrangeClientSum,
			handleClickData: ['10', '20', '30'],
		},
		{
			tooltip: getLabelMessage('semaphore:clientTransferOrangeToRed'),
			fromColor: '#f57f17',
			toColor: '#c21b17',
			data: orangeToRedData,
			sumValue: orangeToRedClientSum,
			handleClickData: ['40', '50', '60'],
		},
		{
			tooltip: getLabelMessage('semaphore:clientTransferRedToGrey'),
			fromColor: '#c21b17',
			toColor: '#6f7072',
			data: redToGrayData,
			sumValue: redToGrayClientSum,
			handleClickData: ['70', '80', '90'],
		},
	];

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<Stack
				direction="row"
				sx={{
					alignItems: 'center',
				}}
			>
				<Typography variant="h5" color="text.primary">
					{getLabelMessage('semaphore:tableImpendingState')}
				</Typography>
				<InfoCircleTooltip sx={{ fontSize: 15 }} text={getLabelMessage('tooltips:semaphoreImpendingState')} />
			</Stack>
			<Table size="small" sx={{ border: 'none !important' }}>
				<TableBody>
					{/* first tableRow substitues the tableHead component, using it because of simpler styling*/}
					<TableRow>
						<TableCell sx={{ borderBottom: 'unset' }} />
						<TableCell align="center">{getLabelMessage('semaphore:tableImpendingStateTo1Month')}</TableCell>
						<TableCell align="center">
							{getLabelMessage('semaphore:tableImpendingStateTo2Months')}
						</TableCell>
						<TableCell align="center">
							{getLabelMessage('semaphore:tableImpendingStateTo3Months')}
						</TableCell>
						<TableCell align="center">{getLabelMessage('semaphore:tableImpendingStateSum')}</TableCell>
					</TableRow>
					{finalData && finalData.map((section) => <ImpendingStateTableRow {...section} />)}
					<TableRow>
						<TableCell align="center" sx={{ borderBottom: 'unset' }}>
							<Tooltip sx={{ fontSize: '30px' }} title={getLabelMessage('semaphore:sum')}>
								<IconButton disableRipple sx={{ color: 'text.primary' }}>
									<Sum />
								</IconButton>
							</Tooltip>
						</TableCell>
						<TableCell align="center" sx={{ borderBottom: 'unset', fontWeight: 'bold' }}>
							{equalOrZero(firstMonth?.sum)}
						</TableCell>
						<TableCell align="center" sx={{ borderBottom: 'unset', fontWeight: 'bold' }}>
							{equalOrZero(secondMonth?.sum)}
						</TableCell>
						<TableCell align="center" sx={{ borderBottom: 'unset', fontWeight: 'bold' }}>
							{equalOrZero(thirdMonth?.sum)}
						</TableCell>
						<TableCell align="center" sx={{ borderBottom: 'unset', fontWeight: 'bold' }}>
							{allMonthsTotalSum}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</Paper>
	);
};

export default ImpendingStateTable;
